import { DayPilot } from '@daypilot/daypilot-lite-react';
import { FR_MONTHS, VIEWS, COLOR_OPTIONS } from './constants';

export function formatFrenchDate(dpDate) {
  const d = new DayPilot.Date(dpDate);
  const month = FR_MONTHS[parseInt(d.toString('M'), 10) - 1];
  return `${month} ${d.toString('yyyy')}`;
}

export function lightenColor(hex, factor = 0.75) {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const lr = Math.round(r + (255 - r) * factor);
  const lg = Math.round(g + (255 - g) * factor);
  const lb = Math.round(b + (255 - b) * factor);
  return '#' + [lr, lg, lb].map((v) => v.toString(16).padStart(2, '0')).join('');
}

export function getStartForView(view, refDate) {
  const d = new DayPilot.Date(refDate);
  if (view === 'week') return d.firstDayOfWeek('fr-fr');
  if (view === 'year') return new DayPilot.Date(d.toString('yyyy') + '-01-01');
  return d.firstDayOfMonth();
}

export function getDaysForView(view, startDate) {
  if (view === 'month') return new DayPilot.Date(startDate).daysInMonth();
  if (view === 'year') {
    const year = parseInt(new DayPilot.Date(startDate).toString('yyyy'), 10);
    return ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) ? 366 : 365;
  }
  return VIEWS[view].days;
}

export function computeLoadForDay(events, resourceId, dayStr) {
  let total = 0;
  const dayStart = new DayPilot.Date(dayStr);
  const dayEnd = dayStart.addDays(1);
  for (const ev of events) {
    if (String(ev.resource) !== String(resourceId)) continue;
    const evStart = new DayPilot.Date(ev.start);
    const evEnd = new DayPilot.Date(ev.end);
    const overlapStart = evStart.getTime() < dayStart.getTime() ? dayStart : evStart;
    const overlapEnd = evEnd.getTime() > dayEnd.getTime() ? dayEnd : evEnd;
    if (overlapStart.getTime() < overlapEnd.getTime()) {
      const hours = (overlapEnd.getTime() - overlapStart.getTime()) / 3600000;
      const wRaw = ev._meta?.workload;
      const workloadFactor = (Number.isFinite(wRaw) ? wRaw : 100) / 100;
      total += hours * workloadFactor;
    }
  }
  return total;
}

/**
 * Sum workload percentages for a resource on a given day.
 * Returns a value where 100 = fully loaded (e.g. 25+40+35 = 100).
 */
export function computeWorkloadForDay(events, resourceId, dayStr) {
  let total = 0;
  const dayStart = new DayPilot.Date(dayStr);
  const dayEnd = dayStart.addDays(1);
  for (const ev of events) {
    if (String(ev.resource) !== String(resourceId)) continue;
    const evStart = new DayPilot.Date(ev.start);
    const evEnd = new DayPilot.Date(ev.end);
    if (evStart.getTime() < dayEnd.getTime() && evEnd.getTime() > dayStart.getTime()) {
      const wVal = ev._meta?.workload;
      total += Number.isFinite(wVal) ? wVal : 100;
    }
  }
  return total;
}

export function detectOverlap(events, resourceId, start, end, excludeId) {
  const s = new DayPilot.Date(start).getTime();
  const e = new DayPilot.Date(end).getTime();
  for (const ev of events) {
    if (ev.id === excludeId) continue;
    if (String(ev.resource) !== String(resourceId)) continue;
    const evS = new DayPilot.Date(ev.start).getTime();
    const evE = new DayPilot.Date(ev.end).getTime();
    if (s < evE && e > evS) return ev;
  }
  return null;
}

export function isPublicHoliday(holidays, dateStr) {
  const d = dateStr.substring(0, 10);
  for (const h of holidays) {
    const hDate = (h.date || '').substring(0, 10);
    if (hDate === d) return h;
    if (h.recurring) {
      const hMD = hDate.substring(5);
      const dMD = d.substring(5);
      if (hMD === dMD) return h;
    }
  }
  return null;
}

export function formatDuration(hours) {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}

export function buildFormFields(prjOptions, ctOptions, resourceOptions, taskOptions, isCreation) {
  return [
    { type: 'title', name: isCreation ? 'Nouvelle affectation' : 'Modifier l\u2019affectation' },
    { name: 'Titre', id: 'text' },
    { name: 'Collaborateur', id: 'resource', type: 'resourceSelect', options: resourceOptions },
    { type: 'title', name: 'Période' },
    { name: 'Début', id: 'start', type: 'date' },
    { name: 'Fin', id: 'end', type: 'date' },
    { type: 'title', name: 'Classification' },
    ...(prjOptions.length ? [{ name: 'Projet', id: 'projectId', type: 'select', options: [{ name: '\u2014 Aucun \u2014', id: '' }, ...prjOptions] }] : []),
    ...(taskOptions.length ? [{ name: 'Tâche', id: 'taskId', type: 'select', dependsOn: 'projectId', filterByField: 'projectId', options: [{ name: '\u2014 Aucune \u2014', id: '', projectId: '' }, ...taskOptions] }] : []),
    ...(ctOptions.length ? [{ name: 'Type personnalisé', id: 'customTaskType', type: 'select', options: [{ name: '\u2014 Aucun \u2014', id: '' }, ...ctOptions] }] : []),
    { name: 'Taux de charge', id: 'workload', type: 'workload' },
    { name: 'Couleur', id: 'backColor', type: 'colorCircles', options: COLOR_OPTIONS },
    { type: 'title', name: 'Détails' },
    { name: 'Notes', id: 'notes', type: 'textarea', height: 80 },
  ];
}

